import {useCallback, useEffect, useRef, useState} from "react";
import {fetchProgramTests, saveProgramTests} from "./dlvApi";

const LOADING_STATE = {status: 'loading', input: '', saved: '', error: null, saving: false};

/**
 * The tests of a program: loaded from the database when the program changes, edited as a draft ("input")
 * and saved on request. The draft differs from what is saved ("dirty") until it is saved.
 */
export function useProgramTests(programName) {
    const [state, setState] = useState({...LOADING_STATE, status: 'idle'});
    const [attempt, setAttempt] = useState(0);
    const currentProgram = useRef(programName);
    currentProgram.current = programName;

    useEffect(() => {
        if (!programName) {
            setState({...LOADING_STATE, status: 'idle'});
            return undefined;
        }
        let mounted = true;
        setState(LOADING_STATE);
        fetchProgramTests(programName)
            .then(response => mounted && setState({...LOADING_STATE, status: 'ready', input: response.content, saved: response.content}))
            .catch(e => mounted && setState({...LOADING_STATE, status: 'error', error: e.message}));
        return () => {
            mounted = false;
        };
    }, [programName, attempt]);

    const setInput = useCallback((value) => {
        setState(s => ({...s, input: typeof value === 'function' ? value(s.input) : value}));
    }, []);

    const retry = useCallback(() => setAttempt(a => a + 1), []);

    /**
     * Saves the draft. Throws if the tests can't be saved; the draft stays as it is.
     */
    const save = useCallback(async (input) => {
        const name = programName;
        setState(s => ({...s, saving: true}));
        try {
            await saveProgramTests(name, input);
        } catch (e) {
            if (currentProgram.current === name) {
                setState(s => ({...s, saving: false}));
            }
            throw e;
        }
        if (currentProgram.current === name) {
            setState(s => ({...s, saving: false, saved: input}));
        }
    }, [programName]);

    return {
        status: state.status,
        error: state.error,
        input: state.input,
        saving: state.saving,
        dirty: state.status === 'ready' && state.input !== state.saved,
        setInput,
        retry,
        save
    };
}
